import React, { Fragment, useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { Card, CardBody, Row, Col, CardHeader } from 'reactstrap';
import { useNavigate } from 'react-router-dom';
import { Breadcrumbs, H3, H4 } from '../../../AbstractElements';
import Button from '../../Common/Elements/Button';
import SweetAlert from 'sweetalert2';
import { toast } from 'react-toastify';
import { useSelector } from 'react-redux';
import { getClientUrlDataToken, postClientUrlWithToken } from '../../../Utils/restUtils';
import { getLeaveType, getLeaveTypeRole, deleteLeaveType, deleteLeaveTypeRole } from '../../../CommonUrl';
import Transparentspinner from '../../Common/Loader/Transparentspinner';

const LeaveType = () => {
  const navigate = useNavigate();
  const authState = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);
  const [leaveTypes, setLeaveTypes] = useState([]);

  const fetchLeaveTypes = async () => {
    setLoading(true);
    try {
      const endpoint = authState.userRole === 1 ? getLeaveType : getLeaveTypeRole;
      const response = await getClientUrlDataToken(authState.apiUrl, endpoint);
      if (response.status === true) {
        setLeaveTypes(response.data.data || []);
      } else {
        toast.error(response.data?.message || "Failed to fetch leave types");
      }
    } catch (error) {
      console.error("Error fetching leave types:", error);
      toast.error("An error occurred while fetching leave types");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLeaveTypes();
  }, [authState.apiUrl, authState.userRole]);

  const handleDelete = (row) => {
    SweetAlert.fire({
      title: 'Confirm deletion?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Ok',
      cancelButtonText: 'Cancel',
      reverseButtons: true
    }).then(async (result) => {
      if (result.value) {
        setLoading(true);
        const payload = { type_id: row.id };
        try {
          const endpoint = authState.userRole === 1 ? deleteLeaveType : deleteLeaveTypeRole;
          const response = await postClientUrlWithToken(authState.apiUrl, endpoint, payload);
          if (response.status === 200) {
            if (response.data.status) {
              SweetAlert.fire('Deleted!', 'Leave type has been deleted.', 'success');
              fetchLeaveTypes();
            } else {
              toast.error(response.data?.message || "Failed to delete leave type");
            }
          } else {
            toast.error("Failed to delete leave type");
          }
        } catch (error) {
          toast.error("An error occurred while deleting leave type");
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const columns = [
    {
      name: <H4>Sl.No</H4>,
      selector: (row, index) => index + 1,
      width: '80px',
      center: true,
    },
    {
      name: <H4>Name</H4>,
      selector: row => row.name,
      sortable: true,
    },
    {
      name: <H4>Type (Paid/Unpaid)</H4>,
      selector: row => row.paid,
      sortable: true,
      cell: row => (
        <span className={row.paid ? "badge-approved" : "badge-negotiation"}>
          {row.paid ? "Paid" : "Unpaid"}
        </span>
      )
    },
    {
      name: <H4>Yearly Limit (Days)</H4>,
      selector: row => row.yearly_limit,
      sortable: true,
      cell: row => <span>{row.yearly_limit} Days</span>
    },
    {
      name: <H4>Actions</H4>,
      center: true,
      cell: row => (
        <div className="d-flex align-items-center gap-2">
          <Button
            className="btn btn-primary py-1 px-3 btn-sm"
            onClick={() => navigate(`${process.env.PUBLIC_URL}/attendance/leave-type/edit?id=${row.id}`)}
          >
            Edit
          </Button>
          <Button
            className="btn btn-danger py-1 px-3 btn-sm"
            onClick={() => handleDelete(row)}
          >
            Delete
          </Button>
        </div>
      )
    }
  ];

  // Compute stats for aesthetic cards at the top
  const totalCount = leaveTypes.length;
  const paidCount = leaveTypes.filter(lt => lt.paid).length;
  const unpaidCount = totalCount - paidCount;

  const stats = [
    { title: 'Total Leave Types', value: totalCount, valueColor: '#111827', iconBg: '#eff6ff' },
    { title: 'Paid Leave Types', value: paidCount, valueColor: '#059669', iconBg: '#ecfdf5' },
    { title: 'Unpaid Leave Types', value: unpaidCount, valueColor: '#d97706', iconBg: '#fef3c7' },
  ];

  return (
    <Fragment>
      {loading && <Transparentspinner />}
      <Breadcrumbs mainTitle="Leave Type" parent="Attendance" title="Leave Type" />
      <div className="container-fluid">
        {/* <Row className="mb-4">
          {stats.map((stat, idx) => (
            <Col key={idx} md={4}>
              <Card className="shadow-sm h-100" style={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                <CardBody className="p-4">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <span style={{ color: '#6b7280', fontSize: '13px', fontWeight: '600' }}>{stat.title}</span>
                      <h3 style={{ color: stat.valueColor, fontWeight: '700', fontSize: '28px', margin: '8px 0', fontFamily: 'Inter, sans-serif' }}>{stat.value}</h3>
                    </div>
                    <div style={{ width: '40px', height: '40px', borderRadius: '8px', backgroundColor: stat.iconBg }}></div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row> */}

        <Card className="shadow-sm" style={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}>
          <CardHeader>
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <H3>Leave Types</H3>
              <Button
                className="btn btn-primary"
                onClick={() => navigate(`${process.env.PUBLIC_URL}/attendance/leave-type/add`)}
              >
                Add Leave Type
              </Button>
            </div>
          </CardHeader>
          <CardBody className="py-3 px-4">
            <DataTable
              columns={columns}
              data={leaveTypes}
              pagination
              defaultSortAsc
              fixedHeader
            />
          </CardBody>
        </Card>
      </div>
    </Fragment>
  );
};

export default LeaveType;